import React, { useEffect, useState } from 'react';
import { Modal } from '@mui/material';
import {
  IconCheck,
  IconArrowRight,
  IconAlertTriangle,
  IconZap,
  IconLock,
  IconWifi,
  IconChevronRight,
} from './SVGIcons';
import PaymentsAPI from 'api/PaymentsAPI';
import CreatePaymentModal from 'components/Plans/CreatePaymentModal';

// Shared between ManageSubscriptions.tsx (the "Manage My Subscriptions" modal)
// and SubscriptionPricing.tsx (the inline Plan Summary) — both let a
// canManageBilling owner change license quantities on already-active plans,
// and both need the exact same proration-preview / payment-step / confirm
// flow so upgrades and downgrades stay billed correctly wherever they're
// triggered from. See useActiveLicenseManager.ts for the state/logic side.

export type ConfirmStep = 'review' | 'payment' | 'confirmed';

export interface ChangeItem {
  label: string;
  detail: string;
  amount?: string;
  type: 'add' | 'remove' | 'change' | 'credit';
}

export const getNetProrationAmount = (invoicePreview: any): number | null => {
  const lines = invoicePreview?.lines?.data;
  if (!Array.isArray(lines)) {
    return null;
  }

  const prorationCents = lines
    .filter((line: any) => Boolean(line?.proration))
    .reduce((total: number, line: any) => total + Number(line?.amount || 0), 0);

  if (prorationCents !== 0) {
    return prorationCents / 100;
  }

  if (typeof invoicePreview?.amount_due === 'number') {
    return invoicePreview.amount_due / 100;
  }

  return null;
};

// ─── Cancel/Renew Modal ─────────────────────────────
export function CancelRenewModal({
  open,
  onClose,
  onConfirm,
  type,
  planName,
  renewsOn,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  type: 'cancel' | 'renew';
  planName: string;
  renewsOn: string;
}) {
  if (!open) return null;
  const isCancel = type === 'cancel';
  return (
    <div className='pricing-overlay' onClick={onClose}>
      <div className='ms-cr-modal' onClick={(e) => e.stopPropagation()}>
        <h3 className='ms-cr-title'>
          {isCancel ? `Cancel ${planName}?` : `Renew ${planName}?`}
        </h3>
        <div
          className='ms-cr-info'
          data-type={isCancel ? 'destructive' : 'primary'}
        >
          <div className='ms-cr-info-main'>
            {isCancel
              ? 'Your subscription will remain active until the end of the current billing period.'
              : 'Your subscription will be renewed and remain active.'}
          </div>
          <div className='ms-cr-info-sub'>
            {isCancel
              ? `Cancels on ${renewsOn}. You won't be charged again after that.`
              : `Renews on ${renewsOn}. Billing will continue as normal.`}
          </div>
        </div>
        <div className='ms-cr-actions'>
          <button
            className='pricing-btn pricing-btn-outline pricing-btn-sm'
            onClick={onClose}
          >
            Keep as-is
          </button>
          <button
            className='ms-cr-confirm'
            data-type={isCancel ? 'destructive' : 'primary'}
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            {isCancel ? 'Yes, cancel' : 'Yes, renew'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Confirm Changes Modal ─────────────────────────────
export function ConfirmModal({
  open,
  onClose,
  changes,
  newTotal,
  onConfirm,
  proratedDueToday,
  requiresPayment,
  customerId,
}: {
  open: boolean;
  onClose: () => void;
  changes: ChangeItem[];
  newTotal: number;
  onConfirm: (paymentMethodId?: string) => Promise<boolean>;
  proratedDueToday?: number | null;
  requiresPayment: boolean;
  customerId?: string;
}) {
  const [step, setStep] = useState<ConfirmStep>('review');
  const [createMode, setCreateMode] = useState(false);
  const [paymentSubmit, setPaymentSubmit] = useState('');
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('');
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [error, setError] = useState('');

  const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`;

  const defaultPaymentMethod =
    paymentMethods.find((method: any) => method?.isDefault) ||
    paymentMethods[0] ||
    null;

  const selectedPaymentMethod =
    paymentMethods.find(
      (method: any) => method?.id === selectedPaymentMethodId,
    ) || defaultPaymentMethod;

  const loadPaymentMethods = async (preferredPaymentMethodId?: string) => {
    if (!customerId) {
      return;
    }

    setPaymentMethodsLoading(true);
    setPaymentError('');
    const paymentMethodsResp = await PaymentsAPI.getCustomerPaymentMethods(
      customerId,
    );
    if (paymentMethodsResp?.success) {
      const methodsResult = paymentMethodsResp.result;
      const normalizedMethods = Array.isArray(methodsResult)
        ? methodsResult
        : Array.isArray(methodsResult?.data)
        ? methodsResult.data
        : [];
      setPaymentMethods(normalizedMethods);
      const nextSelectedPaymentMethodId =
        preferredPaymentMethodId ||
        selectedPaymentMethodId ||
        normalizedMethods[0]?.id ||
        '';
      setSelectedPaymentMethodId(nextSelectedPaymentMethodId);
    } else {
      setPaymentMethods([]);
      setPaymentError('Unable to load payment methods right now.');
    }
    setPaymentMethodsLoading(false);
  };

  useEffect(() => {
    if (!open || !customerId || !requiresPayment) {
      return;
    }

    loadPaymentMethods();
  }, [open, customerId, requiresPayment]);

  useEffect(() => {
    if (!paymentSubmit || !open) {
      return;
    }

    setSelectedPaymentMethodId(paymentSubmit);
    loadPaymentMethods(paymentSubmit);
  }, [paymentSubmit, open]);

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setStep('review');
      setCreateMode(false);
      setPaymentSubmit('');
      setSelectedPaymentMethodId('');
      setPaymentError('');
      setSaving(false);
      setError('');
    }, 300);
  };

  const parseCardBrand = (brand: string) => {
    if (!brand) return 'Card';
    return brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  const canPayWithSavedMethod =
    Boolean(selectedPaymentMethod?.id) && !paymentMethodsLoading;

  const steps: { id: ConfirmStep; label: string }[] = requiresPayment
    ? [
        { id: 'review', label: 'Review' },
        { id: 'payment', label: 'Payment' },
      ]
    : [{ id: 'review', label: 'Review' }];
  const currentStepIndex = steps.findIndex((s) => s.id === step);

  if (!open) return null;

  return (
    <div className='pricing-overlay' onClick={handleClose}>
      <div className='ms-confirm-modal' onClick={(e) => e.stopPropagation()}>
        {step !== 'confirmed' ? (
          <>
            <div className='pricing-modal-header'>
              <h3>Confirm subscription changes</h3>
              <div className='pricing-step-indicator'>
                {steps.map((s, i) => (
                  <div key={s.id} className='ms-step-wrap'>
                    <div
                      className='ms-step-label'
                      data-state={
                        i < currentStepIndex
                          ? 'past'
                          : i === currentStepIndex
                          ? 'current'
                          : 'future'
                      }
                    >
                      <span
                        className='pricing-step-dot'
                        data-state={
                          i < currentStepIndex
                            ? 'completed'
                            : i === currentStepIndex
                            ? 'active'
                            : undefined
                        }
                      >
                        {i < currentStepIndex ? <IconCheck /> : i + 1}
                      </span>
                      {s.label}
                    </div>
                    {i < steps.length - 1 && (
                      <span className='ms-step-chevron'>
                        <IconChevronRight />
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className='ms-confirm-body'>
              {step === 'review' && (
                <div className='ms-review'>
                  <h3 className='ms-review-title'>Changes to your plan</h3>
                  <div className='ms-review-changes'>
                    {changes.map((c, i) => (
                      <div
                        key={i}
                        className='change-row'
                        data-type={
                          c.type === 'remove'
                            ? 'remove'
                            : c.type === 'add'
                            ? 'add'
                            : undefined
                        }
                      >
                        <span className='ms-change-icon'>
                          {c.type === 'add' && (
                            <span className='ms-change-plus'>+</span>
                          )}
                          {c.type === 'remove' && <IconAlertTriangle />}
                          {c.type === 'credit' && <IconZap />}
                          {c.type === 'change' && (
                            <span className='ms-change-tilde'>~</span>
                          )}
                        </span>
                        <div className='ms-change-text'>
                          <div className='ms-change-label'>{c.label}</div>
                          <div className='ms-change-detail'>{c.detail}</div>
                        </div>
                        {c.amount && (
                          <span className='ms-change-amount'>{c.amount}</span>
                        )}
                      </div>
                    ))}
                  </div>
                  <hr className='pricing-separator' />
                  <div className='ms-review-footer'>
                    <div>
                      {typeof proratedDueToday === 'number' ? (
                        <>
                          <div className='ms-review-footer-label'>
                            Due today
                          </div>
                          <div className='ms-review-footer-total'>
                            {formatCurrency(proratedDueToday)}
                          </div>
                          <div className='ms-review-footer-label'>
                            New monthly total: ${newTotal}/mo
                          </div>
                        </>
                      ) : (
                        <>
                          <div className='ms-review-footer-label'>
                            New monthly total
                          </div>
                          <div className='ms-review-footer-total'>
                            ${newTotal}/mo
                          </div>
                        </>
                      )}
                    </div>
                    <button
                      className='pricing-btn pricing-btn-primary'
                      onClick={async () => {
                        setSaving(true);
                        setError('');

                        if (requiresPayment) {
                          setStep('payment');
                          setSaving(false);
                          return;
                        }

                        const success = await onConfirm();
                        if (success) {
                          setStep('confirmed');
                        } else {
                          setError(
                            'Unable to apply subscription changes right now.',
                          );
                        }

                        setSaving(false);
                      }}
                      disabled={saving}
                    >
                      {saving
                        ? 'Saving...'
                        : requiresPayment
                        ? 'Continue'
                        : 'Confirm changes'}{' '}
                      {requiresPayment ? <IconArrowRight /> : <IconCheck />}
                    </button>
                  </div>
                  {error && <div className='ms-error'>{error}</div>}
                </div>
              )}
              {step === 'payment' && (
                <div className='ms-payment'>
                  <div className='ms-payment-header'>
                    <h3 className='ms-payment-title'>Payment details</h3>
                    <div className='ms-stripe-badge'>
                      <IconLock /> Secured by Stripe
                    </div>
                  </div>
                  {paymentMethodsLoading && (
                    <button
                      type='button'
                      className='pricing-radio-option'
                      disabled
                    >
                      <div className='pricing-radio-dot'>
                        <span className='pricing-radio-dot-inner' />
                      </div>
                      <div className='ms-saved-card'>
                        <div className='ms-card-icon'>
                          <IconWifi />
                        </div>
                        <div>
                          <div className='ms-card-name'>
                            Loading payment methods…
                          </div>
                          <div className='ms-card-expiry'>Please wait</div>
                        </div>
                      </div>
                    </button>
                  )}
                  {!paymentMethodsLoading &&
                    paymentMethods.map((method: any) => {
                      const isSelected =
                        method?.id === selectedPaymentMethod?.id;
                      return (
                        <button
                          key={method?.id}
                          type='button'
                          onClick={() =>
                            setSelectedPaymentMethodId(method?.id || '')
                          }
                          className='pricing-radio-option'
                          data-selected={isSelected || undefined}
                        >
                          <div
                            className='pricing-radio-dot'
                            data-selected={isSelected || undefined}
                          >
                            {isSelected && (
                              <span className='pricing-radio-dot-inner' />
                            )}
                          </div>
                          <div className='ms-saved-card'>
                            <div className='ms-card-icon'>
                              <IconWifi />
                            </div>
                            <div>
                              <div className='ms-card-name'>
                                {parseCardBrand(method.card?.brand)} ending in{' '}
                                {method.card?.last4}
                              </div>
                              <div className='ms-card-expiry'>
                                Expires{' '}
                                {String(method.card?.exp_month).padStart(
                                  2,
                                  '0',
                                )}
                                /{method.card?.exp_year}
                              </div>
                            </div>
                          </div>
                          {method?.isDefault && (
                            <span className='ms-default-badge'>Default</span>
                          )}
                          {isSelected && !method?.isDefault && (
                            <span className='ms-default-badge'>Selected</span>
                          )}
                        </button>
                      );
                    })}
                  {paymentError && (
                    <div className='ms-error'>{paymentError}</div>
                  )}
                  {!paymentMethodsLoading &&
                    !selectedPaymentMethod &&
                    !paymentError && (
                      <div className='ms-error'>
                        You need a payment method before applying these changes.
                      </div>
                    )}
                  <button
                    type='button'
                    className='pricing-btn pricing-btn-outline'
                    onClick={() => setCreateMode(true)}
                  >
                    Add payment method
                  </button>
                  <div className='ms-payment-footer'>
                    <button
                      className='pricing-btn pricing-btn-outline'
                      onClick={() => setStep('review')}
                    >
                      Back
                    </button>
                    <button
                      className='pricing-btn pricing-btn-primary'
                      onClick={async () => {
                        setSaving(true);
                        setError('');
                        const success = await onConfirm(
                          selectedPaymentMethod?.id,
                        );
                        if (success) {
                          setStep('confirmed');
                        } else {
                          setError(
                            'Unable to apply subscription changes right now.',
                          );
                        }
                        setSaving(false);
                      }}
                      disabled={saving || !canPayWithSavedMethod}
                    >
                      {saving ? 'Saving...' : 'Confirm changes'} <IconLock />
                    </button>
                  </div>
                  {error && <div className='ms-error'>{error}</div>}
                </div>
              )}
            </div>
            <Modal open={createMode} onClose={() => setCreateMode(false)}>
              <CreatePaymentModal
                cusId={customerId || ''}
                setCreateMode={setCreateMode}
                paymentSubmit={paymentSubmit}
                setPaymentSubmit={setPaymentSubmit}
              />
            </Modal>
          </>
        ) : (
          <div className='ms-confirmed'>
            <div className='ms-confirmed-icon'>
              <IconCheck />
            </div>
            <h2 className='ms-confirmed-title'>Changes saved!</h2>
            <div className='ms-confirmed-text'>
              Your subscription has been updated. Changes take effect
              immediately.
            </div>
            <div className='ms-confirmed-receipt'>
              {changes.map((c, i) => (
                <div key={i} className='ms-confirmed-item'>
                  <span className='ms-change-icon'>
                    {c.type === 'add' && (
                      <span className='ms-change-plus'>+</span>
                    )}
                    {c.type === 'remove' && <IconAlertTriangle />}
                    {c.type === 'credit' && <IconZap />}
                    {c.type === 'change' && (
                      <span className='ms-change-tilde'>~</span>
                    )}
                  </span>
                  <span className='ms-confirmed-item-label'>{c.label}</span>
                  {c.amount && (
                    <span className='ms-confirmed-item-amount'>{c.amount}</span>
                  )}
                </div>
              ))}
              <hr className='pricing-separator' />
              <div className='ms-confirmed-total'>
                <span>New total</span>
                <span>${newTotal}/mo</span>
              </div>
              {typeof proratedDueToday === 'number' && (
                <div className='ms-confirmed-total'>
                  <span>Paid today (prorated)</span>
                  <span>{formatCurrency(proratedDueToday)}</span>
                </div>
              )}
            </div>
            <button
              className='pricing-btn pricing-btn-primary'
              onClick={handleClose}
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
